import _ from "lodash";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  CurrencyContext,
  StyleTypeContext,
  TranslationContext,
} from "../../../umbilical_tools";
import PropTypes from "prop-types";
import Prebook from "../Prebook";
import FlightsForm from "../FlightsForm";
import TravellersForms from "../TravellersForms";
import { Button } from "../../common/Button";
import { toast } from "react-toastify";
import { transfersErrorMsgs } from "../../../errors";
import {
  flightsDtlsObj,
  getRequiredFields,
  onTransferBooking,
  onTransferPreBook,
} from "./helpers";
import jssVariables from "../../../jss_setup";
import Stepper from "../../common/Stepper";
import { useApoAuth, useBaseReservationPayload } from "../../../hooks";
import {
  onFailedToCreateBookingEntry,
  paymentDispatcher,
} from "../../../actions/book/payment";
import { useBookingProcess } from "../../../hooks/booking";
import ExternallyHostedPayment from "../../common/StripeHostedPayment";
import BarclaysPayment from "../../common/payment/barclays";
import { gaussianRounding, getExchangedPrice } from "../../../umbilical_hocs";
import { getPriceDecimalsSelector } from "../../../selectors";

const styles = createUseStyles({
  bookingProcess: (props) => ({
    position: "fixed",
    height: "100%",
    width: "100vw",
    display: "grid",
    gridTemplateRows: "auto 1fr auto",
    background: jssVariables.colors.background.light,
    padding: jssVariables.normal_gap,
    justifyContent: "center",
    zIndex: 9999,
    ..._.get(props, "customStyles.bookingProcess", {}),
  }),
  content: (props) => ({
    boxShadow: jssVariables.shadows.normalShadow,
    borderRadius: "10px",
    padding: `calc(${jssVariables.normal_gap} * 1.5)`,
    height: "max-content",
    maxHeight: "70vh",
    minWidth: "90vw",
    overflowY: "scroll",
    background: "white",
    ..._.get(props, "customStyles.content", {}),
  }),
  actions: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    justifyContent: "end",
    gridGap: `calc(${jssVariables.normal_gap} * 2)`,
    paddingTop: `calc(${jssVariables.normal_gap} * 2)`,
    ..._.get(props, "customStyles.actions", {}),
  }),
  placeholderBtn: (props) => ({
    height: "2rem!important",
    width: "6rem!important",
    ..._.get(props, "customStyles.placeholderBtn", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    content: (props) => ({
      minWidth: "35rem",
      maxWidth: "62rem",
      height: "max-content",
      maxHeight: "90vh",
      marginTop: `calc(${jssVariables.normal_gap} * 2)`,
      overflowY: "unset",
      padding: `${jssVariables.double_gap} calc(${jssVariables.normal_gap} * 3)`,

      ..._.get(props, "customStyles.content.smallscreen", {}),
    }),
    actions: (props) => ({
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      justifyContent: "end",
      gridGap: `calc(${jssVariables.normal_gap} * 2)`,
      paddingTop: `calc(${jssVariables.normal_gap} * 2)`,
      ..._.get(props, "customStyles.actions.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {},
});
const TransferBookingProcess = ({ page_session_id }) => {
  const {
    payment_supplier,
    loading,
    step,
    onIncreaseStep,
    onDecreaseStep,
    onIdle,
    onLoading,
  } = useBookingProcess({ initialLoading: false });

  const [flightsDetails, setFlightsDetails] = useState(flightsDtlsObj);
  const [guests, setGuests] = useState([]);
  const [preBookResult, setPreBookResult] = useState({});
  const [agreed, setAgreed] = useState(false);

  var { source_entity, market_type } = useApoAuth();

  const { t } = useContext(TranslationContext);
  const history = useHistory();
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const { currency: settedCurrency } = useContext(CurrencyContext);
  const priceDecimals = useSelector((state) => getPriceDecimalsSelector(state));

  const classes = styles({
    customStyles: cStyler({
      type,
      brand,
      objName: "TransferBookingProcess",
    }),
  });

  const { transfer, tripMode, search_session_id, website_meta } = useSelector(
    (state) => {
      const { api_key, name } = state.WebsiteMetadata.metadata;

      return {
        transfer: _.get(state.TransferSelected, "transfer", null),
        tripMode: _.get(state.TransfersSearchForm, "tripMode", ""),
        search_session_id: state.SessionIds.TRF.search_session_id,
        website_meta: { api_key, name },
      };
    }
  );

  const {
    first_name,
    last_name,
    email,
    customer_uid,
    nationality,
    customer_stripe_id,
  } = useBaseReservationPayload({
    group_leader: guests.find((g) => g.group_leader),
  });

  function onBackToTrfList() {
    history.push(`/transfers/${page_session_id}`);
  }

  function validateFlightData() {
    const isFormValid = getRequiredFields(tripMode).every(
      (field) => !_.isEmpty(flightsDetails[field])
    );
    if (!isFormValid) {
      toast.warning(transfersErrorMsgs.formValidation.missing_fields, {
        duration: 3000,
      });
      return;
    }
    onIncreaseStep();
  }

  function validateGuests() {
    const isFormValid = guests.every((guest) =>
      Object.values(guest).every((field) => !!field)
    );
    if (!isFormValid) {
      toast.warning(transfersErrorMsgs.formValidation.missing_fields, {
        duration: 3000,
      });
      return;
    }
    onIncreaseStep();
  }

  const dispatch = useDispatch();
  const onBook = useCallback(
    (payload) =>
      dispatch(async () => {
        const result = await onTransferBooking(payload);

        if (!result) {
          onFailedToCreateBookingEntry({ history });
          return;
        }

        const booking_result = _.get(result, "booking_result");
        const booking_reference = _.get(booking_result, "reference");
        const amount = booking_result["payment_data"]["amount"];
        const currency = booking_result["payment_data"]["currency"];

        var exchangedAmount = getExchangedPrice({
          price: amount,
          priceCurrency: currency,
          settedCurrency,
        });
        exchangedAmount = gaussianRounding(exchangedAmount, priceDecimals);

        const srv_type = "TRF";
        const order_id = `${srv_type}___${booking_reference}`;
        const base_url = window.location.origin;
        const success_url = `${base_url}/transfer/successful-booking/${page_session_id}`;
        const cancel_url = `${base_url}/transfer/booking_process/${page_session_id}`;
        const fail_url = `${base_url}/failed-booking/${page_session_id}`;

        if (transfer.is_own && market_type === "B2B") {
          window.location.href = success_url;
          return;
        }

        dispatch(
          paymentDispatcher({
            booking_reference,
            page_session_id,
            amount: exchangedAmount,
            currency: settedCurrency,
            first_name,
            last_name,
            email,
            customer_uid,
            nationality,
            customer_stripe_id,
            order_id,
            success_url,
            cancel_url,
            fail_url,
            srv_type,
            source_entity,
          })
        );
      }),
    [dispatch, first_name, last_name, email]
  );

  // Prebook
  useEffect(() => {
    if (_.isEmpty(transfer)) {
      onBackToTrfList();
    } else {
      onLoading();
      onTransferPreBook({
        transfer,
        search_session_id,
        onCancel: onBackToTrfList,
      }).then((result) => {
        setPreBookResult(result);
        onIdle();
      });
    }
  }, []);

  // Book
  useEffect(() => {
    if (step < 3) {
      return;
    }

    onBook({
      market_type,
      website_meta,
      source_entity,
      session_id: search_session_id,
      transfer,
      preBookResult,
      flightsDtls: flightsDetails,
      guests,
      tripMode,
      customer_user_data: {
        first_name,
        last_name,
        email,
        customer_uid,
        nationality,
        customer_stripe_id,
      },
    });
  }, [step]);

  return (
    <div className={classes.bookingProcess}>
      <div className={classes.stepper}>
        <Stepper
          steps={[t("prebook"), t("travelInfo"), t("book")]}
          current={step === 4 ? 2 : step}
        />
      </div>
      <div className={classes.content}>
        {!_.isEmpty(transfer) && (
          <React.Fragment>
            {step === 0 && (
              <Prebook
                loading={loading}
                preBookResult={preBookResult}
                cxls={_.get(preBookResult, "cancellation_policies", [])}
                agreed={agreed}
                setAgreed={setAgreed}
              />
            )}
            {step === 1 && (
              <FlightsForm
                flightsDtls={flightsDetails}
                setFlightsDtls={setFlightsDetails}
              />
            )}
            {step === 2 && (
              <TravellersForms guests={guests} setGuests={setGuests} />
            )}
            {step === 3 && payment_supplier !== "BCR" && (
              <ExternallyHostedPayment />
            )}
            {step === 3 && payment_supplier === "BCR" && <BarclaysPayment />}
          </React.Fragment>
        )}
      </div>
      <div className={classes.actions}>
        <Button
          id="previousStepBtn"
          text={step > 0 ? t("previousStep") : t("cancel")}
          bold={true}
          size="large"
          onClick={() => (step > 0 ? onDecreaseStep() : onBackToTrfList())}
        />
        <Button
          id="nextStepBtn"
          text={step < 2 ? t("nextStep") : t("payment")}
          bold={true}
          size="large"
          onClick={() => {
            if (step === 0 && !agreed) {
              toast.warning(t("pleaseCheckTheBox"));
              return;
            }
            switch (step) {
              case 1:
                validateFlightData();
                break;
              case 2:
                validateGuests();
                break;
              default:
                onIncreaseStep();
                break;
            }
          }}
        />
      </div>
    </div>
  );
};
TransferBookingProcess.defaultProps = {
  page_session_id: false,
};
TransferBookingProcess.propTypes = {
  page_session_id: PropTypes.string.isRequired,
};
export default TransferBookingProcess;
